import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { ModalAddPP } from '../ModalAddPP';

const COLUMNS = [
    { key: 'discount',       label: 'Descuento',    field: 'descuento'   },
    { key: 'initialPayment', label: 'Enganche',      field: 'enganche'    },
    { key: 'monthlyPayment', label: 'Mensualidad',   field: 'mensualidad' },
    { key: 'giveProperty',   label: 'Entrega',       field: 'entrega'     },
    { key: 'monthsNumber',   label: 'Meses',         field: 'meses'       },
];

const EMPTY_FILTERS = { descuento: '', enganche: '', mensualidad: '', entrega: '', meses: '' };

function SortIcon({ active, dir }) {
    if (!active) return <span className="text-gray-300 text-[10px] leading-none select-none">↕</span>;
    return <span className="text-nemu-azul text-[10px] leading-none select-none">{dir === 'asc' ? '↑' : '↓'}</span>;
}

function ModalSelectPaymentPlans({ isOpen, onClose, selectedPlanIds, onConfirm, deliveryDate, allPlans, onPlanCreated }) {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [localSelected, setLocalSelected] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [planIdsBefore, setPlanIdsBefore] = useState(null);

    // Cuando allPlans se actualiza después de crear, auto-selecciona el plan nuevo
    useEffect(() => {
        if (planIdsBefore === null) return;
        const newPlans = allPlans.filter(p => !planIdsBefore.has(p.id));
        if (newPlans.length > 0) {
            setLocalSelected(prev => {
                const toAdd = newPlans.map(p => p.id).filter(id => !prev.includes(id));
                return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
            });
            setPlanIdsBefore(null);
        }
    }, [allPlans, planIdsBefore]);

    const handleOpenCreate = () => {
        setPlanIdsBefore(new Set(allPlans.map(p => p.id)));
        setShowCreateModal(true);
    };

    const handleCreateClose = () => {
        setShowCreateModal(false);
        onPlanCreated?.();
    };

    useEffect(() => {
        if (isOpen) {
            setLocalSelected([...selectedPlanIds]);
            setFilters(EMPTY_FILTERS);
            setSortField(null);
            setSortDir('asc');
        }
    }, [isOpen]);

    const handleSort = (key) => {
        if (sortField === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(key);
            setSortDir('asc');
        }
    };

    const filteredPlans = useMemo(() => {
        let result = allPlans.filter(plan =>
            (filters.descuento   === '' || String(plan.discount).includes(filters.descuento))        &&
            (filters.enganche    === '' || String(plan.initialPayment).includes(filters.enganche))    &&
            (filters.mensualidad === '' || String(plan.monthlyPayment).includes(filters.mensualidad)) &&
            (filters.entrega     === '' || String(plan.giveProperty).includes(filters.entrega))       &&
            (filters.meses       === '' || String(plan.monthsNumber).includes(filters.meses))
        );

        if (sortField) {
            result = [...result].sort((a, b) => {
                const va = Number(a[sortField]) ?? 0;
                const vb = Number(b[sortField]) ?? 0;
                return sortDir === 'asc' ? va - vb : vb - va;
            });
        }

        return result;
    }, [allPlans, filters, sortField, sortDir]);

    const togglePlan = (plan) => {
        const isSelected = localSelected.includes(plan.id);

        if (!isSelected) {
            if (plan.monthsNumber > 0 && deliveryDate) {
                const today = new Date();
                const [deliveryYear, deliveryMonth] = deliveryDate.split('-').map(Number);
                const diffMonths = (deliveryYear - today.getFullYear()) * 12 + (deliveryMonth - 1 - today.getMonth());

                if (diffMonths < plan.monthsNumber) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Plan de pagos no válido',
                        text: `La fecha de entrega es menor al número de meses del plan (${plan.monthsNumber} meses). Ajusta la fecha de entrega o elige otro plan.`,
                    });
                    return;
                }
            }
            setLocalSelected(prev => [...prev, plan.id]);
        } else {
            setLocalSelected(prev => prev.filter(id => id !== plan.id));
        }
    };

    const handleConfirm = () => {
        onConfirm(localSelected);
        onClose();
    };

    if (!isOpen) return null;

    const inputCls = "w-full px-2 py-1 border border-gray-200 rounded text-[12px] focus:outline-none focus:border-nemu-azul focus:ring-1 focus:ring-nemu-azul placeholder:text-gray-300";

    return (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-[100]">
            <div className="bg-white rounded-lg w-[760px] max-h-[85vh] flex flex-col shadow-xl">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-xl font-medium">Seleccionar planes de pago</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">&times;</button>
                </div>

                {/* Filtros */}
                <div className="px-6 py-4 border-b bg-gray-50">
                    <div className="grid grid-cols-5 gap-3">
                        {COLUMNS.map(col => (
                            <div key={col.key} className="flex flex-col gap-1">
                                <button
                                    type="button"
                                    onClick={() => handleSort(col.key)}
                                    className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-nemu-azul transition-colors w-fit"
                                >
                                    {col.label}
                                    <SortIcon active={sortField === col.key} dir={sortDir} />
                                </button>
                                <input
                                    type="number"
                                    min="0"
                                    max={col.key === 'monthsNumber' ? undefined : 100}
                                    value={filters[col.field]}
                                    onChange={e => setFilters(prev => ({ ...prev, [col.field]: e.target.value }))}
                                    placeholder="—"
                                    className={inputCls}
                                />
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">
                        {filteredPlans.length} de {allPlans.length} planes · haz clic en el nombre de columna para ordenar
                    </p>
                </div>

                {/* Lista de planes */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {allPlans.length === 0 ? (
                        <p className="text-center text-gray-400 py-8">Cargando planes...</p>
                    ) : filteredPlans.length === 0 ? (
                        <p className="text-center text-gray-400 py-8">Sin resultados para los filtros aplicados</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {filteredPlans.map(plan => {
                                const isSelected = localSelected.includes(plan.id);
                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => togglePlan(plan)}
                                        className={`flex items-center gap-4 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                                            isSelected
                                                ? 'bg-black text-white border-black'
                                                : 'border-gray-200 hover:border-gray-400 bg-white'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            onClick={e => e.stopPropagation()}
                                            className="w-4 h-4 shrink-0 cursor-pointer"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="font-semibold font-mono">{plan.name}</span>
                                            </div>
                                            <div className={`flex flex-wrap gap-x-4 gap-y-1 text-sm ${isSelected ? 'text-gray-300' : 'text-gray-600'}`}>
                                                <span>Descuento: <strong>{plan.discount}%</strong></span>
                                                <span>Enganche: <strong>{plan.initialPayment}%</strong></span>
                                                <span>Mensualidad: <strong>{plan.monthlyPayment}%</strong></span>
                                                <span>A entrega: <strong>{plan.giveProperty}%</strong></span>
                                                <span>Meses: <strong>{plan.monthsNumber > 0 ? plan.monthsNumber : 'N/A'}</strong></span>
                                            </div>
                                        </div>
                                    </div>
                                );
            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t">
                    <span className="text-sm text-gray-500">
                        {localSelected.length} plan{localSelected.length !== 1 ? 'es' : ''} seleccionado{localSelected.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                            Cancelar
                        </button>
                        <button type="button" onClick={handleConfirm} className="px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800">
                            Confirmar selección
                        </button>
                    </div>
                </div>

                {/* Crear plan personalizado */}
                <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
                    <span className="text-[12px] text-gray-400">¿No encuentras un plan que se ajuste?</span>
                    <button
                        type="button"
                        onClick={handleOpenCreate}
                        className="flex items-center gap-1.5 px-4 py-2 border border-nemu-azul text-nemu-azul rounded-lg text-sm font-medium hover:bg-nemu-azul hover:text-white transition-colors"
                    >
                        + Crear plan personalizado
                    </button>
                </div>
            </div>

            {showCreateModal && (
                <ModalAddPP closeModal={handleCreateClose} planToModify={false} />
            )}
        </div>
    );
}

export { ModalSelectPaymentPlans };
